import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import { type MessageComposerHandle } from '@precis-digital/kurama/MessageLayout';
import { useRef, type ReactElement } from 'react';
import { useCopilot } from 'shared/context/CopilotContext';
import { useTranslation } from 'shared/translations';
import { CopilotSurfaceProvider } from 'shared/copilot/CopilotSurfaceContext';
import { useFocusComposerWhenDrawerOpens } from 'shared/copilot/hooks/useFocusComposerWhenDrawerOpens';
import { CopilotChatHistoryActionButtons, CopilotChatHistoryDropdown, CopilotChatShell, useCopilotChatHistoryControls } from './CopilotChatShell';
import CopilotDrawerEmptyState from './CopilotDrawerEmptyState';
import { CopilotLocationChip } from './CopilotLocationChip';
import CopilotAgentSwitcher from './CopilotAgentSwitcher';
import { CopilotDatasourceControls } from './CopilotDatasourceControls';
import { useCopilotSuggestedPromptsAccessory } from './CopilotSuggestedPrompts';

/**
 * Sidenav Copilot chat: page agent (`alvie_copilot`) — knowledge sources, page chip, history.
 */
const CopilotDrawerChatView = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CopilotSurfaceProvider surface="drawer"><DrawerChatContent /></CopilotSurfaceProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const DrawerChatContent = () => {
  const $ = _c(11);
  const {
    sendUserMessage
  } = useCopilot();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "copilot"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("common", t0);
  const suggestedPromptsAccessory = useCopilotSuggestedPromptsAccessory();
  const composerRef = useRef(null);
  useFocusComposerWhenDrawerOpens(composerRef);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      window.setTimeout(() => {
        composerRef.current?.focus();
      }, 0);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const focusComposer = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CopilotDrawerEmptyState />;
    t3 = <DrawerMessageControls onKnowledgeMenuClosed={focusComposer} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("inputPlaceholder");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== sendUserMessage || $[8] !== suggestedPromptsAccessory || $[9] !== t4) {
    t6 = <CopilotChatShell emptyState={t2} messageControls={t3} inputPlaceholder={t4} onSubmit={sendUserMessage} composerRef={composerRef} composerActions={t5} enableSkills={false} emptyComposerTopAccessory={suggestedPromptsAccessory} />;
    $[7] = sendUserMessage;
    $[8] = suggestedPromptsAccessory;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
const DrawerMessageControls = t0 => {
  const $ = _c(25);
  const {
    onKnowledgeMenuClosed
  } = t0;
  const {
    dropdownAnchorEl,
    chatHistoryGroupedByDate,
    isHistoryLoading,
    isHistoryError,
    handleClickHistory,
    handleCloseHistoryPopup,
    handleClickNewChat,
    handleSelectChat,
    resolveBucketLabel
  } = useCopilotChatHistoryControls();
  let t1;
  if ($[0] !== chatHistoryGroupedByDate || $[1] !== dropdownAnchorEl || $[2] !== handleCloseHistoryPopup || $[3] !== handleSelectChat || $[4] !== isHistoryError || $[5] !== isHistoryLoading || $[6] !== resolveBucketLabel) {
    t1 = <CopilotChatHistoryDropdown dropdownAnchorEl={dropdownAnchorEl} chatHistoryGroupedByDate={chatHistoryGroupedByDate} isLoading={isHistoryLoading} isError={isHistoryError} onClose={handleCloseHistoryPopup} onSelectChat={handleSelectChat} resolveBucketLabel={resolveBucketLabel} />;
    $[0] = chatHistoryGroupedByDate;
    $[1] = dropdownAnchorEl;
    $[2] = handleCloseHistoryPopup;
    $[3] = handleSelectChat;
    $[4] = isHistoryError;
    $[5] = isHistoryLoading;
    $[6] = resolveBucketLabel;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== handleClickNewChat) {
    t2 = <CopilotAgentSwitcher onSwitch={handleClickNewChat} />;
    $[8] = handleClickNewChat;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== onKnowledgeMenuClosed) {
    t3 = <CopilotDatasourceControls variant="knowledge-only" onMenuClosed={onKnowledgeMenuClosed} />;
    $[10] = onKnowledgeMenuClosed;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CopilotLocationChip />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3) {
    t5 = <Grid display="flex" flexDirection="row" alignItems="center" gap="4px" flexWrap="wrap">{t2}{t3}{t4}</Grid>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== handleClickHistory || $[17] !== handleClickNewChat) {
    t6 = <CopilotChatHistoryActionButtons onNewChat={handleClickNewChat} onHistoryClick={handleClickHistory} />;
    $[16] = handleClickHistory;
    $[17] = handleClickNewChat;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <Grid display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">{t5}{t6}</Grid>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t1 || $[23] !== t7) {
    t8 = <>{t1}{t7}</>;
    $[22] = t1;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
export default CopilotDrawerChatView;